import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  FileText, Download, MessageSquare, Send, CheckCircle, Clock, 
  AlertTriangle, XCircle, Loader2, CreditCard, DollarSign, Calendar,
  Building, Mail, Phone, User, Lock, ThumbsUp, ThumbsDown, Receipt, X, Printer, ExternalLink, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { toast, Toaster } from 'sonner';
import StripePaymentForm from '@/components/StripePaymentForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  draft: { color: 'bg-slate-500', icon: FileText, label: 'Draft' },
  sent: { color: 'bg-blue-500', icon: Send, label: 'Sent' },
  viewed: { color: 'bg-purple-500', icon: FileText, label: 'Awaiting Payment' },
  partial: { color: 'bg-amber-500', icon: Clock, label: 'Partially Paid' },
  paid: { color: 'bg-emerald-500', icon: CheckCircle, label: 'Paid' },
  expired: { color: 'bg-red-500', icon: XCircle, label: 'Expired' },
  canceled: { color: 'bg-gray-600', icon: XCircle, label: 'Canceled' },
  cancelled: { color: 'bg-gray-600', icon: XCircle, label: 'Cancelled' },
  declined: { color: 'bg-red-500', icon: XCircle, label: 'Declined' }
};


const InvoicePaymentPage = () => {
  const { invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const isAdminViewer = searchParams.get('viewer') === 'admin';
  
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [processing, setProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  
  // Quote accept/decline state
  const [acceptingQuote, setAcceptingQuote] = useState(false);
  const [decliningQuote, setDecliningQuote] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [quoteAccepted, setQuoteAccepted] = useState(false);
  const [newInvoiceId, setNewInvoiceId] = useState(null);
  
  // Use ref to store payment amount for stable callback
  const paymentAmountRef = useRef(paymentAmount);
  const invoiceRef = useRef(invoice);
  
  // Keep refs in sync
  useEffect(() => {
    paymentAmountRef.current = paymentAmount;
  }, [paymentAmount]);
  
  useEffect(() => {
    invoiceRef.current = invoice;
  }, [invoice]);
  
  // Comment state
  const [comment, setComment] = useState('');
  const [commenterName, setCommenterName] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  
  // Stripe config
  const [stripeConfig, setStripeConfig] = useState(null);
  const [stripeOrderId, setStripeOrderId] = useState(null);
  const [creatingOrder, setCreatingOrder] = useState(false);
  
  // Receipt modal state
  const [showReceipt, setShowReceipt] = useState(false);
  
  // Fetch invoice - admin viewing should NOT mark as viewed or change status
  const fetchInvoice = useCallback(async () => {
    try {
      const viewerParam = isAdminViewer ? 'admin' : 'customer';
      const markViewed = isAdminViewer ? 'false' : 'true';
      const response = await axios.get(`${API}/invoices/${invoiceId}?mark_viewed=${markViewed}&viewer=${viewerParam}`);
      setInvoice(response.data);
      setPaymentAmount(response.data.amount_due?.toFixed(2) || '');
      setCommenterName(`${response.data.customer?.first_name || ''} ${response.data.customer?.last_name || ''}`.trim());
    } catch (err) {
      setError(err.response?.data?.detail || 'Invoice not found');
    } finally {
      setLoading(false);
    }
  }, [invoiceId, isAdminViewer]);
  
  // Fetch Stripe config
  const fetchStripeConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/payment-config`);
      setStripeConfig(response.data);
    } catch (err) {
      console.error('Failed to load payment config:', err);
    }
  }, []);
  
  useEffect(() => {
    fetchInvoice();
    fetchStripeConfig();
  }, [fetchInvoice, fetchStripeConfig]);
  
  // Stable createStripeOrder that uses refs - called manually when Pay button is clicked
  const handleCreateOrder = useCallback(async () => {
    const amount = parseFloat(paymentAmountRef.current);
    const currentInvoice = invoiceRef.current;
    
    if (!amount || amount <= 0 || !currentInvoice) {
      toast.error('Please enter a valid payment amount');
      return null;
    }
    
    if (amount > currentInvoice.amount_due) {
      toast.error(`Amount cannot exceed $${currentInvoice.amount_due.toFixed(2)}`);
      return null;
    }
    
    if (!currentInvoice.allow_partial_payments && amount < currentInvoice.amount_due) {
      toast.error('This invoice requires full payment');
      return null;
    }
    
    if (currentInvoice.allow_partial_payments && currentInvoice.minimum_payment && amount < currentInvoice.minimum_payment) {
      toast.error(`Minimum payment is $${currentInvoice.minimum_payment.toFixed(2)}`);
      return null;
    }
    
    setCreatingOrder(true);
    try {
      const response = await axios.post(`${API}/invoices/${invoiceId}/create-payment`, {
        amount
      });
      setStripeOrderId(response.data.order_id);
      return response.data.order_id;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create payment');
      return null;
    } finally {
      setCreatingOrder(false);
    }
  }, [invoiceId]); // Only depends on invoiceId which doesn't change
  
  // Dummy createOrder for StripeHostedFieldsProvider - actual order created in handleCreateOrder
  const createStripeOrder = useCallback(async () => {
    // Return the existing order ID if we have one, or create a new one
    if (stripeOrderId) {
      return stripeOrderId;
    }
    return await handleCreateOrder();
  }, [stripeOrderId, handleCreateOrder]);
  
  // Memoize Stripe options to prevent re-initialization
  const stripeOptions = useMemo(() => {
    if (!stripeConfig?.client_id || !stripeConfig?.client_token) return null;
    return {
      clientId: stripeConfig.client_id,
      currency: "USD",
      components: "hosted-fields",
      dataClientToken: stripeConfig.client_token,
      intent: "capture"
    };
  }, [stripeConfig?.client_id, stripeConfig?.client_token]);
  
  // Memoize Stripe field styles
  const stripeFieldStyles = useMemo(() => ({
    '.valid': { 'color': '#10b981' },
    '.invalid': { 'color': '#ef4444' },
    'input': { 
      'font-size': '16px',
      'color': '#ffffff',
      'font-family': 'inherit'
    }
  }), []);
  
  // Handle successful payment
  const onApprove = useCallback(async (data) => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/invoices/${invoiceId}/capture-payment`, {
        stripe_order_id: data.orderID,
        amount: parseFloat(paymentAmountRef.current)
      });
      
      if (response.data.success) {
        setPaymentSuccess(true);
        setStripeOrderId(null); // Reset order ID for potential future payments
        toast.success('Payment successful!');
        fetchInvoice(); // Refresh to show updated amount due
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  }, [invoiceId, fetchInvoice]);
  
  const onError = useCallback((error) => {
    console.error('Payment error:', error);
    const errorMessage = error?.message || 'Payment failed. Please try again.';
    toast.error(errorMessage, { duration: 6000 });
    setProcessing(false);
    setStripeOrderId(null); // Reset order ID on error
  }, []);
  
  // Add comment
  const addComment = async () => {
    if (!comment.trim()) {
      toast.error('Enter a comment');
      return;
    }
    
    setSendingComment(true);
    try {
      await axios.post(`${API}/invoices/${invoiceId}/comment`, {
        message: comment,
        author: 'customer',
        author_name: commenterName || 'Customer'
      });
      toast.success('Comment added');
      setComment('');
      fetchInvoice();
    } catch (err) {
      toast.error('Failed to add comment');
    } finally {
      setSendingComment(false);
    }
  };
  
  // Accept quote - converts to invoice
  const acceptQuote = async () => {
    setAcceptingQuote(true);
    try {
      const response = await axios.post(`${API}/invoices/${invoiceId}/accept-quote`);
      toast.success(response.data.message);
      setQuoteAccepted(true);
      setNewInvoiceId(response.data.new_invoice_id);
      // Redirect to new invoice page
      window.location.href = `/invoice/${response.data.new_invoice_id}`;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept quote');
    } finally {
      setAcceptingQuote(false);
    }
  };
  
  // Decline quote
  const declineQuote = async () => {
    setDecliningQuote(true);
    try {
      await axios.post(`${API}/invoices/${invoiceId}/decline-quote`, {
        reason: declineReason
      });
      toast.success('Quote declined. Thank you for your feedback.');
      setShowDeclineDialog(false);
      fetchInvoice(); // Refresh to show declined status
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline quote');
    } finally {
      setDecliningQuote(false);
    }
  };
  
  // Download PDF
  const downloadPdf = async () => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-teal-400" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Invoice Not Found</h1>
          <p className="text-slate-400">{error}</p>
        </div>
      </div>
    );
  }
  
  const statusConfig = STATUS_CONFIG[invoice.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusConfig.icon;
  const isPaid = invoice.status === 'paid';
  const isExpired = invoice.status === 'expired';
  const isCanceled = invoice.status === 'canceled' || invoice.status === 'cancelled';
  const canPay = !isPaid && !isExpired && !isCanceled && invoice.invoice_type === 'invoice';
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <div className="bg-slate-800/80 backdrop-blur border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white">Biggest Little CPR</h1>
              <p className="text-slate-400 mt-1">
                {invoice.invoice_type === 'quote' ? 'Quote' : 'Invoice'} #{invoice.invoice_id}
              </p>
            </div>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${statusConfig.color}`}>
              <StatusIcon className="h-5 w-5 text-white" />
              <span className="text-white font-medium">{statusConfig.label}</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Invoice Details */}
          <div className="md:col-span-2 space-y-6">
            {/* Customer Info */}
            <Card className="bg-slate-800/50 border-slate-700 p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <User className="h-5 w-5 text-teal-400" />
                Bill To
              </h2>
              <div className="space-y-2 text-slate-300">
                <p className="font-medium text-white">
                  {invoice.customer?.first_name} {invoice.customer?.last_name}
                </p>
                {invoice.customer?.company && (
                  <p className="flex items-center gap-2">
                    <Building className="h-4 w-4 text-slate-500" />
                    {invoice.customer.company}
                  </p>
                )}
                <p className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-slate-500" />
                  {invoice.customer?.email}
                </p>
                {invoice.customer?.phone && (
                  <p className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-slate-500" />
                    {invoice.customer.phone}
                  </p>
                )}
              </div>
            </Card>
            
            {/* Items */}
            <Card className="bg-slate-800/50 border-slate-700 p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Items</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 text-slate-400 font-medium">Description</th>
                      <th className="text-center py-3 text-slate-400 font-medium">Qty</th>
                      <th className="text-right py-3 text-slate-400 font-medium">Price</th>
                      <th className="text-right py-3 text-slate-400 font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.items?.map((item, idx) => (
                      <tr key={idx} className="border-b border-slate-700/50">
                        <td className="py-4">
                          <p className="text-white font-medium">{item.name || item.description}</p>
                          {item.description && item.name && (
                            <p className="text-sm text-slate-400">{item.description}</p>
                          )}
                        </td>
                        <td className="py-4 text-center text-slate-300">{item.quantity}</td>
                        <td className="py-4 text-right text-slate-300">${item.unit_price?.toFixed(2)}</td>
                        <td className="py-4 text-right text-white font-medium">
                          ${(item.quantity * item.unit_price)?.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Totals */}
              <div className="mt-6 pt-4 border-t border-slate-700 space-y-2">
                <div className="flex justify-between text-slate-400">
                  <span>Subtotal</span>
                  <span>${invoice.subtotal?.toFixed(2)}</span>
                </div>
                {invoice.discount_amount > 0 && (
                  <div className="flex justify-between text-emerald-400">
                    <span>Discount ({invoice.discount?.reason || 'Applied'})</span>
                    <span>-${invoice.discount_amount?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xl font-bold text-white pt-2 border-t border-slate-700">
                  <span>Total</span>
                  <span>${invoice.total?.toFixed(2)}</span>
                </div>
                {invoice.amount_paid > 0 && (
                  <>
                    <div className="flex justify-between text-emerald-400">
                      <span>Amount Paid</span>
                      <span>${invoice.amount_paid?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-amber-400 text-lg font-semibold">
                      <span>Amount Due</span>
                      <span>${invoice.amount_due?.toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>
            </Card>
            
            {/* Notes */}
            {invoice.customer_notes && (
              <Card className="bg-slate-800/50 border-slate-700 p-6">
                <h2 className="text-lg font-semibold text-white mb-3">Notes</h2>
                <p className="text-slate-300 whitespace-pre-wrap">{invoice.customer_notes}</p>
              </Card>
            )}
            
            {/* Terms */}
            {invoice.terms_and_conditions && (
              <Card className="bg-slate-800/50 border-slate-700 p-6">
                <h2 className="text-lg font-semibold text-white mb-3">Terms & Conditions</h2>
                <p className="text-slate-400 text-sm whitespace-pre-wrap">{invoice.terms_and_conditions}</p>
              </Card>
            )}
            
            {/* Comments */}
            <Card className="bg-slate-800/50 border-slate-700 p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-teal-400" />
                Comments
              </h2>
              
              {invoice.comments?.length > 0 && (
                <div className="space-y-3 mb-6">
                  {invoice.comments.map((c, idx) => (
                    <div 
                      key={idx} 
                      className={`p-4 rounded-lg ${c.author === 'admin' ? 'bg-teal-900/30 border border-teal-700/50' : 'bg-slate-700/50'}`}
                    >
                      <div className="flex justify-between text-sm mb-2">
                        <span className={c.author === 'admin' ? 'text-teal-400' : 'text-slate-400'}>
                          {c.author_name} {c.author === 'admin' && '(Admin)'}
                        </span>
                        <span className="text-slate-500">{formatDate(c.created_at)}</span>
                      </div>
                      <p className="text-white">{c.message}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add Comment */}
              <div className="space-y-3">
                <div>
                  <Label className="text-slate-400">Your Name</Label>
                  <Input
                    value={commenterName}
                    onChange={(e) => setCommenterName(e.target.value)}
                    placeholder="Your name"
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-400">Message</Label>
                  <Textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Have a question? Leave a comment..."
                    className="bg-slate-700 border-slate-600 text-white h-24"
                  />
                </div>
                <Button
                  onClick={addComment}
                  disabled={sendingComment || !comment.trim()}
                  className="bg-teal-600 hover:bg-teal-500"
                >
                  {sendingComment ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Send Comment
                </Button>
              </div>
            </Card>
          </div>
          
          {/* Payment Sidebar */}
          <div className="space-y-6">
            {/* Payment Card */}
            <Card className="bg-slate-800/50 border-slate-700 p-6 sticky top-6">
              {isPaid ? (
                <div className="text-center py-6">
                  <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-10 w-10 text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Payment Complete</h3>
                  <p className="text-slate-400">Thank you for your payment!</p>
                  {invoice.woo_order_id && (
                    <p className="text-sm text-teal-400 mt-2">Order #{invoice.woo_order_id}</p>
                  )}
                  
                  {/* View Receipt Button */}
                  <Button
                    onClick={() => setShowReceipt(true)}
                    className="mt-4 bg-slate-700 hover:bg-slate-600 text-white"
                  >
                    <Receipt className="h-4 w-4 mr-2" />
                    View Payment Receipt
                  </Button>
                </div>
              ) : isExpired ? (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <XCircle className="h-10 w-10 text-red-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Invoice Expired</h3>
                  <p className="text-slate-400">This invoice has expired. Please contact us for assistance.</p>
                </div>
              ) : isCanceled ? (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-gray-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <XCircle className="h-10 w-10 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Invoice Canceled</h3>
                  <p className="text-slate-400">This invoice has been canceled and is no longer payable.</p>
                  {invoice.cancel_reason && (
                    <p className="text-slate-500 text-sm mt-2">Reason: {invoice.cancel_reason}</p>
                  )}
                  <p className="text-slate-500 text-sm mt-4">Please contact us if you have any questions.</p>
                </div>
              ) : invoice.invoice_type === 'quote' && invoice.status === 'declined' ? (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <XCircle className="h-10 w-10 text-red-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Quote Declined</h3>
                  <p className="text-slate-400">You have declined this quote.</p>
                  {invoice.decline_reason && (
                    <p className="text-slate-500 text-sm mt-2">Reason: {invoice.decline_reason}</p>
                  )}
                </div>
              ) : invoice.invoice_type === 'quote' ? (
                <div className="py-6">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileText className="h-8 w-8 text-purple-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Quote Review</h3>
                    <p className="text-slate-400 text-sm">Review the details and accept to proceed with payment.</p>
                  </div>
                  
                  {/* Quote Total Summary */}
                  <div className="bg-slate-700/50 rounded-lg p-4 mb-6">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Quote Total</span>
                      <span className="text-2xl font-bold text-white">${invoice.total?.toFixed(2)}</span>
                    </div>
                    {invoice.expiration_date && (
                      <div className="flex justify-between items-center mt-2 pt-2 border-t border-slate-600">
                        <span className="text-slate-500 text-sm">Valid Until</span>
                        <span className="text-amber-400 text-sm">{formatDate(invoice.expiration_date)}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Accept/Decline Buttons */}
                  <div className="space-y-3">
                    <Button
                      onClick={acceptQuote}
                      disabled={acceptingQuote || decliningQuote}
                      className="w-full h-12 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
                    >
                      {acceptingQuote ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin mr-2" />
                          Converting to Invoice...
                        </>
                      ) : (
                        <>
                          <ThumbsUp className="h-5 w-5 mr-2" />
                          Accept Quote & Pay
                        </>
                      )}
                    </Button>
                    
                    {!showDeclineDialog ? (
                      <Button
                        onClick={() => setShowDeclineDialog(true)}
                        disabled={acceptingQuote}
                        variant="outline"
                        className="w-full h-12 border-red-500/50 text-red-400 hover:bg-red-500/10 hover:border-red-500"
                      >
                        <ThumbsDown className="h-5 w-5 mr-2" />
                        Decline Quote
                      </Button>
                    ) : (
                      <div className="bg-slate-700/50 rounded-lg p-4 space-y-3">
                        <Label className="text-slate-400">Reason for declining (optional)</Label>
                        <Textarea
                          value={declineReason}
                          onChange={(e) => setDeclineReason(e.target.value)}
                          placeholder="Let us know why this quote doesn't work for you..."
                          className="bg-slate-800 border-slate-600 text-white h-20"
                        />
                        <div className="flex gap-2">
                          <Button
                            onClick={() => setShowDeclineDialog(false)}
                            variant="outline"
                            className="flex-1 border-slate-600"
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={declineQuote}
                            disabled={decliningQuote}
                            className="flex-1 bg-red-600 hover:bg-red-500"
                          >
                            {decliningQuote ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              'Confirm Decline'
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <p className="text-slate-500 text-xs text-center mt-4">
                    By accepting, this quote will be converted to an invoice for payment.
                  </p>
                </div>
              ) : paymentSuccess ? (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-10 w-10 text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Payment Successful!</h3>
                  <p className="text-slate-400">Thank you for your payment.</p>
                  {invoice.status === 'partial' && invoice.amount_due > 0 && (
                    <div className="mt-4 p-4 bg-amber-500/20 border border-amber-500/50 rounded-lg">
                      <p className="text-amber-400 font-medium">Remaining Balance: ${invoice.amount_due?.toFixed(2)}</p>
                      <Button 
                        onClick={() => {
                          setPaymentSuccess(false);
                          setPaymentAmount(invoice.amount_due?.toFixed(2) || '');
                        }}
                        className="mt-3 bg-amber-600 hover:bg-amber-500"
                      >
                        Make Another Payment
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-teal-400" />
                    Make Payment
                  </h3>
                  
                  {/* Amount Due */}
                  <div className="bg-slate-700/50 rounded-lg p-4 mb-4">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Amount Due</span>
                      <span className="text-2xl font-bold text-white">${invoice.amount_due?.toFixed(2)}</span>
                    </div>
                  </div>
                  
                  {/* Payment Amount */}
                  {invoice.allow_partial_payments && (
                    <div className="mb-4">
                      <Label className="text-slate-400">Payment Amount</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                        <Input
                          type="number"
                          min={invoice.minimum_payment || 0}
                          max={invoice.amount_due}
                          step="0.01"
                          value={paymentAmount}
                          onChange={(e) => setPaymentAmount(e.target.value)}
                          className="pl-10 bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                      {invoice.minimum_payment && (
                        <p className="text-xs text-slate-500 mt-1">
                          Minimum payment: ${invoice.minimum_payment?.toFixed(2)}
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Stripe Inline Payment */}
                  <div className="space-y-4">
                    <StripePaymentForm
                      amount={parseFloat(paymentAmount || invoice.amount_due)}
                      description={`Invoice ${invoice.invoice_number || invoiceId || 'Payment'}`}
                      customerEmail={invoice.client_email}
                      metadata={{
                        invoice_id: invoiceId,
                        invoice_number: invoice.invoice_number || invoiceId,
                        payment_type: 'invoice'
                      }}
                      onSuccess={async (result) => {
                        setProcessing(true);
                        try {
                          // Update invoice payment status
                          const amount = parseFloat(paymentAmount || invoice.amount_due);
                          await axios.post(`${API}/invoices/${invoiceId}/record-payment`, {
                            amount,
                            payment_method: 'stripe',
                            payment_intent_id: result.paymentIntentId,
                            transaction_id: result.paymentIntentId
                          });
                          
                          setPaymentSuccess(true);
                          toast.success('Payment successful! Thank you.');
                          // Refresh invoice
                          fetchInvoice();
                        } catch (error) {
                          console.error('Error recording payment:', error);
                          toast.error('Payment processed but failed to update invoice. Please contact support.');
                        } finally {
                          setProcessing(false);
                        }
                      }}
                      onError={(error) => {
                        console.error('Payment error:', error);
                        toast.error(error.message || 'Payment failed. Please try again.');
                      }}
                      disabled={processing}
                    />
                  </div>
                </>
              )}
              
              {/* Dates */}
              {(invoice.due_date || invoice.expiration_date) && (
                <div className="mt-4 pt-4 border-t border-slate-700 space-y-2 text-sm">
                  {invoice.due_date && (
                    <div className="flex items-center gap-2 text-slate-400">
                      <Calendar className="h-4 w-4" />
                      <span>Due: {formatDate(invoice.due_date)}</span>
                    </div>
                  )}
                  {invoice.expiration_date && (
                    <div className="flex items-center gap-2 text-amber-400">
                      <AlertTriangle className="h-4 w-4" />
                      <span>Expires: {formatDate(invoice.expiration_date)}</span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Download PDF */}
              <div className="mt-4 pt-4 border-t border-slate-700">
                <Button
                  variant="outline"
                  onClick={downloadPdf}
                  className="w-full border-slate-600 text-white hover:bg-slate-700"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="bg-slate-800/50 border-t border-slate-700 mt-12">
        <div className="max-w-4xl mx-auto px-6 py-6 text-center">
          <p className="text-slate-500 text-sm">
            Biggest Little CPR • Professional CPR Training<br />
            Questions? Contact us at info@biggestlittlecpr.com
          </p>
        </div>
      </div>
      
      {/* Payment Receipt Modal */}
      <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
        <DialogContent className="bg-white text-black max-w-md p-0 overflow-hidden border-0">
          {/* Receipt styled like a store receipt */}
          <div className="receipt-container" id="payment-receipt">
            {/* Receipt Header with torn edge effect */}
            <div className="bg-white px-6 pt-6 pb-4 text-center border-b-2 border-dashed border-gray-300">
              <h1 className="text-2xl font-bold tracking-wider mb-1">RECEIPT</h1>
              <div className="text-lg font-semibold">Biggest Little CPR</div>
              <div className="text-xs text-gray-500 mt-1">Professional CPR Training</div>
              <div className="text-xs text-gray-500">info@biggestlittlecpr.com</div>
            </div>
            
            {/* Receipt Body */}
            <div className="bg-white px-6 py-4 font-mono text-sm">
              {/* Transaction Info */}
              <div className="text-center mb-4 pb-3 border-b border-dashed border-gray-300">
                <div className="text-xs text-gray-500">Invoice #</div>
                <div className="font-bold">{invoice?.invoice_id}</div>
                <div className="text-xs text-gray-500 mt-2">
                  {invoice?.paid_at ? new Date(invoice.paid_at).toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : formatDate(invoice?.updated_at)}
                </div>
              </div>
              
              {/* Customer Info */}
              <div className="mb-4 pb-3 border-b border-dashed border-gray-300">
                <div className="text-xs text-gray-500 mb-1">BILL TO:</div>
                <div className="font-semibold">
                  {invoice?.customer?.first_name} {invoice?.customer?.last_name}
                </div>
                {invoice?.customer?.company && (
                  <div className="text-xs">{invoice.customer.company}</div>
                )}
                <div className="text-xs">{invoice?.customer?.email}</div>
              </div>
              
              {/* Items */}
              <div className="mb-4">
                <div className="text-xs text-gray-500 mb-2">ITEMS:</div>
                <div className="space-y-2">
                  {invoice?.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between">
                      <div className="flex-1 pr-2">
                        <div className="text-sm">{item.name || item.description}</div>
                        <div className="text-xs text-gray-500">
                          {item.quantity} x ${item.unit_price?.toFixed(2)}
                        </div>
                      </div>
                      <div className="text-right font-semibold">
                        ${(item.quantity * item.unit_price)?.toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Totals Section */}
              <div className="border-t border-dashed border-gray-300 pt-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>${invoice?.subtotal?.toFixed(2)}</span>
                </div>
                {invoice?.discount_amount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount</span>
                    <span>-${invoice.discount_amount?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-300">
                  <span>TOTAL</span>
                  <span>${invoice?.total?.toFixed(2)}</span>
                </div>
              </div>
              
              {/* Payment Details */}
              <div className="mt-4 pt-3 border-t border-dashed border-gray-300">
                <div className="text-xs text-gray-500 mb-2">PAYMENT DETAILS:</div>
                {invoice?.payments?.length > 0 ? (
                  <div className="space-y-2">
                    {invoice.payments.map((payment, idx) => (
                      <div key={idx} className="bg-gray-50 p-2 rounded text-xs">
                        <div className="flex justify-between font-semibold">
                          <span>{payment.payment_method?.toUpperCase() || 'PAYMENT'}</span>
                          <span className="text-green-600">${payment.amount?.toFixed(2)}</span>
                        </div>
                        {payment.transaction_id && (
                          <div className="text-gray-500 mt-1 break-all">
                            Trans: {payment.transaction_id}
                          </div>
                        )}
                        {payment.capture_id && (
                          <div className="text-gray-500 break-all">
                            Capture: {payment.capture_id}
                          </div>
                        )}
                        <div className="text-gray-400 mt-1">
                          {payment.created_at && new Date(payment.created_at).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 text-xs py-2">
                    Marked as paid by admin
                  </div>
                )}
                
                <div className="flex justify-between font-bold mt-3 pt-2 border-t border-gray-300 text-green-600">
                  <span>AMOUNT PAID</span>
                  <span>${invoice?.amount_paid?.toFixed(2)}</span>
                </div>
                
                {invoice?.amount_due > 0 && (
                  <div className="flex justify-between font-bold text-amber-600">
                    <span>BALANCE DUE</span>
                    <span>${invoice.amount_due?.toFixed(2)}</span>
                  </div>
                )}
              </div>
              
              {/* Order Reference */}
              {invoice?.woo_order_id && (
                <div className="mt-4 pt-3 border-t border-dashed border-gray-300 text-center">
                  <div className="text-xs text-gray-500">Order Reference</div>
                  <div className="font-bold">#{invoice.woo_order_id}</div>
                </div>
              )}
              
              {/* Thank You Message */}
              <div className="mt-6 text-center">
                <div className="text-lg font-bold mb-1">Thank You!</div>
                <div className="text-xs text-gray-500">We appreciate your business</div>
              </div>
              
              {/* Receipt Footer */}
              <div className="mt-4 pt-3 border-t border-dashed border-gray-300 text-center text-xs text-gray-400">
                <div>Keep this receipt for your records</div>
                <div className="mt-1">* * * * * * * * * * * * * *</div>
              </div>
            </div>
          </div>
          
          {/* Print/Close Buttons */}
          <div className="bg-gray-100 px-6 py-4 flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowReceipt(false)}
              className="flex-1"
            >
              Close
            </Button>
            <Button
              onClick={() => {
                const receiptEl = document.getElementById('payment-receipt');
                if (receiptEl) {
                  const printWindow = window.open('', '_blank');
                  printWindow.document.write(`
                    <html>
                      <head>
                        <title>Receipt - ${invoice?.invoice_id}</title>
                        <style>
                          body { font-family: 'Courier New', monospace; max-width: 400px; margin: 0 auto; padding: 20px; }
                          .receipt-container { background: white; }
                          .text-center { text-align: center; }
                          .font-bold { font-weight: bold; }
                          .text-xs { font-size: 10px; }
                          .text-sm { font-size: 12px; }
                          .text-lg { font-size: 16px; }
                          .text-2xl { font-size: 20px; }
                          .mb-1 { margin-bottom: 4px; }
                          .mb-2 { margin-bottom: 8px; }
                          .mb-4 { margin-bottom: 16px; }
                          .mt-1 { margin-top: 4px; }
                          .mt-2 { margin-top: 8px; }
                          .mt-3 { margin-top: 12px; }
                          .mt-4 { margin-top: 16px; }
                          .mt-6 { margin-top: 24px; }
                          .pt-2 { padding-top: 8px; }
                          .pt-3 { padding-top: 12px; }
                          .pb-3 { padding-bottom: 12px; }
                          .pb-4 { padding-bottom: 16px; }
                          .px-6 { padding-left: 24px; padding-right: 24px; }
                          .py-4 { padding-top: 16px; padding-bottom: 16px; }
                          .pt-6 { padding-top: 24px; }
                          .p-2 { padding: 8px; }
                          .border-dashed { border-style: dashed; }
                          .border-b { border-bottom: 1px dashed #ccc; }
                          .border-t { border-top: 1px dashed #ccc; }
                          .border-gray-300 { border-color: #d1d5db; }
                          .text-gray-500 { color: #6b7280; }
                          .text-gray-400 { color: #9ca3af; }
                          .text-green-600 { color: #059669; }
                          .text-amber-600 { color: #d97706; }
                          .bg-gray-50 { background: #f9fafb; }
                          .rounded { border-radius: 4px; }
                          .flex { display: flex; }
                          .justify-between { justify-content: space-between; }
                          .flex-1 { flex: 1; }
                          .pr-2 { padding-right: 8px; }
                          .space-y-1 > * + * { margin-top: 4px; }
                          .space-y-2 > * + * { margin-top: 8px; }
                          .break-all { word-break: break-all; }
                          .tracking-wider { letter-spacing: 0.1em; }
                          @media print { body { margin: 0; } }
                        </style>
                      </head>
                      <body>
                        ${receiptEl.innerHTML}
                      </body>
                    </html>
                  `);
                  printWindow.document.close();
                  printWindow.print();
                }
              }}
              className="flex-1 bg-teal-600 hover:bg-teal-500 text-white"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Receipt
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoicePaymentPage;
